import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import isEqual from "lodash.isequal";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { addBidChoiceEvent, fetchLatestBidChoiceEventByReferenceNumberAndEid } from "../../api/bid.js";
import { fetchCurrentEmployeeMonthlyScheduleMap } from "../../api/scheduleEditor.js";
import PageLoading from "../../components/PageLoading.jsx";
import BidChoicesTable from "../../components/bid/BidChoicesTable.jsx";
import BidTimer from "../../components/bid/BidTimer.jsx";
import BidInfoDialog from "../../components/fundamentals/BidInfoDialog.jsx";
import ConfirmButton from "../../components/fundamentals/ConfirmButton.jsx";
import ConfirmationDialog from "../../components/fundamentals/ConfirmationDialog.jsx";
import SlideUpTransition from "../../components/fundamentals/SlideUpTransition.js";
import StarsBreadcrumbs from "../../components/fundamentals/StarsBreadcrumbs.jsx";
import SuccessDialog from "../../components/fundamentals/SuccessDialog.jsx";
import { selectUserSession } from "../../slices/userSession/userSessionSlice.js";
import {
  BID_STATUS,
  BID_TYPE,
  EMPLOYEE_BID_STATUS,
  NC_PTO_FTO_BID_TIME_OFF_TYPES,
} from "../../util/bid/bidConstants.js";
import { buildBidChoiceEvent, prepareNewBidChoiceEventPayload } from "../../util/bid/bidEventBuilder.js";
import { getScrubbedBidEvent } from "../../util/bid/bidScrubber.js";
import { getBidName, getDisabledDatesBasedOnSchedule, getNonEmptyChoices } from "../../util/bid/timeOffBidUtils.js";
import { getEidFromHref, getReferenceNumberFromHref } from "../../util/bid/shiftBidUtils.js";
import { BREADCRUMBS_TITLE, CANCEL_WARNING_MESSAGE_ACTION_TYPE, FEATURES, HOME_PATH } from "../../util/constants.js";
import { convertMonthStringToInteger } from "../../util/dateTimeFormatter.js";
import {
  BID_CHOICE_SAVE_ERROR_MESSAGE,
  BID_FORM_CANCEL_MESSAGE,
  BID_FORM_CLEAR_ALL,
  BID_FORM_CLEAR_ALL_CHOICES_MESSAGE,
  PTO_FTO_BID_CHOICE_CONFIRMATION_MESSAGE,
  PTO_FTO_BID_CHOICE_CONFIRMATION_TITLE,
  LAST_UPDATED,
  LAST_UPDATED_TIMESTAMP,
} from "../../util/messageConstants.js";
import { getStationTimeZoneDateTimeStringFromUTCInteger } from "../../util/timeZoneService.js";
import { validatePTOCount, validateFTOCount } from "../../util/bid/bidInputValidation.js";
import NCBidPDFExport from "./NCBidPDFExport.jsx";

const useStyles = makeStyles((theme) => ({
  lastSubmitted: {
    textAlign: "left",
    color: theme.palette.secondary.swaGray4,
    marginRight: "3.5vw",
  },
  addChoice: {
    margin: "15px",
  },
  inputText: {
    width: "110px",
    textAlign: "left",
    paddingRight: "10px",
    "& .MuiInputBase-root": {
      background: "white",
    },
  },
  bidInput: {
    paddingLeft: "10px",
  },
}));

const NonContractPTOFTOBidChoices = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isBidChoicesLoading, setIsBidChoicesLoading] = useState(true);

  // Bid Choice Data State
  const [bidChoiceData, setBidChoiceData] = useState(null);
  const [selectedChoices, setSelectedChoices] = useState([]);
  const [initialSelectedChoices, setInitialSelectedChoices] = useState([]);
  const [ptoCount, setPtoCount] = useState("");
  const [ptoCountErrors, setPtoCountErrors] = useState("");
  const [initialPtoCount, setInitialPtoCount] = useState("");
  const [ftoCount, setFtoCount] = useState("");
  const [ftoCountErrors, setFtoCountErrors] = useState("");
  const [initialFtoCount, setInitialFtoCount] = useState("");

  // Dialog state
  const [isClearAllDialogOpen, setIsClearAllDialogOpen] = useState(false);
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const [isGuideDialogOpen, setIsGuideDialogOpen] = useState(false);
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [isSubmitResponseDialogOpen, setIsSubmitResponseDialogOpen] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [submitErrors, setSubmitErrors] = useState("");
  const [timeOffTypeErrors, setTimeOffTypeErrors] = useState("");

  const [lastUpdatedByFirstName, setLastUpdatedByFirstName] = useState("");
  const [lastUpdatedByLastName, setLastUpdatedByLastName] = useState("");

  // Schedule state
  const [disabledDates, setDisabledDates] = useState([]);
  const [isScheduleLoading, setIsScheduleLoading] = useState(true);

  const [isReadOnly, setIsReadOnly] = useState(false);

  useEffect(() => {
    fetchLatestPTOFTOBidChoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isDesktopView]);

  useEffect(() => {
    if (bidChoiceData) {
      fetchSchedule(bidChoiceData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidChoiceData]);

  useEffect(() => {
    // validate choices when any of the counts is present
    if (ptoCount || ftoCount) {
      validatePtoFtoCountWithChoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChoices]);

  const handlePTOCountEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handlePTOCountBlur();
    }
  };

  const handlePTOCountChange = (event) => {
    const input = event.target.value;
    const sanitizedInput = input.replace(/\D/g, "");
    setPtoCount(sanitizedInput);
    setPtoCountErrors("");
  };

  const handlePTOCountBlur = () => {
    const selectedNonEmptyChoices = getNonEmptyChoices(selectedChoices);
    const numOfPTOChoices = selectedNonEmptyChoices.filter(
      (choice) => choice.timeOffType === NC_PTO_FTO_BID_TIME_OFF_TYPES.PTO
    ).length;
    const errors = validatePTOCount(ptoCount, numOfPTOChoices);

    if (errors === "") {
      setPtoCountErrors("");
    } else {
      setPtoCountErrors(errors);
    }
  };

  const handleFTOCountEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleFTOCountBlur();
    }
  };

  const handleFTOCountChange = (event) => {
    const input = event.target.value;
    const sanitizedInput = input.replace(/\D/g, "");
    setFtoCount(sanitizedInput);
    setFtoCountErrors("");
  };

  const handleFTOCountBlur = () => {
    const selectedNonEmptyChoices = getNonEmptyChoices(selectedChoices);
    const numOfFTOChoices = selectedNonEmptyChoices.filter(
      (choice) => choice.timeOffType === NC_PTO_FTO_BID_TIME_OFF_TYPES.FTO
    ).length;
    const errors = validateFTOCount(ftoCount, numOfFTOChoices);

    if (errors === "") {
      setFtoCountErrors("");
    } else {
      setFtoCountErrors(errors);
    }
  };

  let history = useHistory();

  const fetchSchedule = ({ eid, station, schedYear, schedMonth }) => {
    if (eid && station && schedYear && schedMonth) {
      setIsScheduleLoading(true);
      fetchCurrentEmployeeMonthlyScheduleMap(eid, station, schedYear, convertMonthStringToInteger(schedMonth))
        .then((res) => res.json())
        .then((response) => {
          if (response?.event) {
            const disabledDates = getDisabledDatesBasedOnSchedule({
              scheduledMap: response.event,
              station: station,
              bidType: BID_TYPE.NC_PTO_FTO,
              schedMonth: schedMonth,
              schedYear: schedYear,
            });
            setDisabledDates(disabledDates);
            setIsScheduleLoading(false);
          } else {
            throw Error();
          }
        })
        .catch((err) => {
          setIsScheduleLoading(false);
          console.error(`Bid Error: %j`, err);
        });
    }
  };

  const fetchLatestPTOFTOBidChoices = () => {
    const eid = getEidFromHref();
    const referenceNumber = getReferenceNumberFromHref();
    fetchLatestBidChoiceEventByReferenceNumberAndEid(eid, referenceNumber)
      .then((res) => res.json())
      .then((response) => {
        if (response?.event) {
          const bidChoiceEvent = getScrubbedBidEvent(response.event);
          setBidChoiceData(bidChoiceEvent);

          const choices =
            response.event?.choices?.length > 0 ? response.event.choices : [{ date: "", timeOffType: "" }];
          setSelectedChoices(choices);
          setInitialSelectedChoices(choices);

          const ptoCount = response.event.ptoCount ? response.event.ptoCount : "";
          setPtoCount(ptoCount);
          setInitialPtoCount(ptoCount);

          const ftoCount = response.event.ftoCount ? response.event.ftoCount : "";
          setFtoCount(ftoCount);
          setInitialFtoCount(ftoCount);

          setLastUpdatedByFirstName(response.event?.modifiedByFirstName);
          setLastUpdatedByLastName(response.event?.modifiedByLastName);
          setIsPageLoading(false);
          setIsBidChoicesLoading(false);
          if (bidChoiceEvent.status === BID_STATUS.CLOSED) {
            setSelectedChoices(response.event?.choices?.length > 0 ? response.event.choices : []);
            setIsReadOnly(true);
          }
        } else {
          throw Error();
        }
      })
      .catch((err) => {
        console.error(`Bid Error: %j`, err);
        setIsPageLoading(false);
      });
  };

  const handleSubmitClicked = () => {
    setIsSubmitLoading(true);
    console.log("Saving bid choice data...");
    let payload = buildBidChoiceEvent(
      prepareNewBidChoiceEventPayload({ bidChoiceData, selectedChoices, ftoCount, ptoCount }),
      userSession
    );
    addBidChoiceEvent(payload)
      .then((res) => res.json())
      .then((result) => {
        if (result.message) {
          console.error("Error: ", result.message);
          setSubmitErrors(result.message);
          setIsSubmitLoading(false);
          setIsSubmitDialogOpen(false);
          setIsSubmitResponseDialogOpen(true);
        } else {
          console.log("Success!");
          setIsSubmitDialogOpen(false);
          setIsSubmitLoading(false);
          setIsSubmitResponseDialogOpen(true);
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsSubmitDialogOpen(false);
        setIsSubmitLoading(false);
      });
  };

  const validatePtoFtoCountWithChoices = () => {
    let isFormValid = false;
    const selectedNonEmptyChoices = getNonEmptyChoices(selectedChoices);
    const numOfPTOChoices = selectedNonEmptyChoices.filter(
      (choice) => choice.timeOffType === NC_PTO_FTO_BID_TIME_OFF_TYPES.PTO
    ).length;
    const numOfFTOChoices = selectedNonEmptyChoices.filter(
      (choice) => choice.timeOffType === NC_PTO_FTO_BID_TIME_OFF_TYPES.FTO
    ).length;

    const ptoErrors = validatePTOCount(ptoCount, numOfPTOChoices);
    const ftoErrors = validateFTOCount(ftoCount, numOfFTOChoices);

    setPtoCountErrors(ptoErrors);
    setFtoCountErrors(ftoErrors);

    if (ptoErrors === "" && ftoErrors === "") {
      isFormValid = true;
    }

    return isFormValid;
  };

  const handleClickSubmitDialogOpen = () => {
    const isValidChoicesWithCounts = validatePtoFtoCountWithChoices();
    const selectedNonEmptyChoices = getNonEmptyChoices(selectedChoices);
    // validate all choices are having timeoff type value or not
    const missingTimeOffTypes = selectedNonEmptyChoices.filter((choice) => choice.timeOffType === "");
    if (missingTimeOffTypes.length > 0) {
      setTimeOffTypeErrors("Time Off Type Required");
    } else {
      setTimeOffTypeErrors("");
    }
    if (isValidChoicesWithCounts && missingTimeOffTypes.length === 0) {
      setSelectedChoices(selectedNonEmptyChoices);
      // open submit dialog when all validations pass
      setIsSubmitDialogOpen(true);
    }
  };

  const handleClickSubmitDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsSubmitDialogOpen(false);
  };

  const handleClickCancelDialogOpen = () => {
    if (
      !isEqual(getNonEmptyChoices(selectedChoices), getNonEmptyChoices(initialSelectedChoices)) ||
      !isEqual(ftoCount, initialFtoCount) ||
      !isEqual(ptoCount, initialPtoCount)
    ) {
      setIsExitDialogOpen(true);
    } else {
      // no changes made, so re-direct to my bids
      history.push(getMyBidsPath());
    }
  };

  const handleClickCancelDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsExitDialogOpen(false);
  };

  const handleClickGuideDialogOpen = () => {
    setIsGuideDialogOpen(true);
  };

  const handleCloseClickedGuideDialog = () => {
    setIsGuideDialogOpen(false);
  };

  const handleClickClearAllDialogOpen = () => {
    setIsClearAllDialogOpen(true);
  };

  const handleClickCancelClearAllDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsClearAllDialogOpen(false);
  };

  const handleClearAllChoices = () => {
    setSelectedChoices([{ date: "", timeOffType: "" }]);
    setTimeOffTypeErrors("");
    setIsClearAllDialogOpen(false);
  };

  const getLastUpdatedTimeString = () => {
    return getStationTimeZoneDateTimeStringFromUTCInteger(getLastSubmittedTimestamp(), bidChoiceData.station).toFormat(
      "MM/dd/yyyy T ZZZZ"
    );
  };

  const getMyBidsPath = () => {
    return props.featureConfig.childItems.find((childItem) => childItem.key === FEATURES.MY_BIDS).path;
  };

  const getLastSubmittedTimestamp = () => {
    return bidChoiceData?.createdAt ? Date.parse(bidChoiceData.createdAt) : "";
  };

  const isSubmitButtonDisabled = () => {
    if (bidChoiceData.status !== EMPLOYEE_BID_STATUS.OPEN) {
      return true;
    }

    // No changes have been made from the latest submission
    return !!(
      isEqual(getNonEmptyChoices(selectedChoices), getNonEmptyChoices(initialSelectedChoices)) &&
      isEqual(ftoCount, initialFtoCount) &&
      isEqual(ptoCount, initialPtoCount)
    );
  };

  const renderDesktopNavBtnsForBidForm = () => {
    return (
      <>
        {isReadOnly ? (
          <>
            <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
              <NCBidPDFExport bidChoiceData={bidChoiceData} buttonVariant="outlined" />
            </Box>
            <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
              <Button
                sx={{
                  width: "100px",
                }}
                variant="contained"
                color="primary"
                onClick={() =>
                  history.push({
                    pathname: getMyBidsPath(),
                  })
                }
              >
                Close
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
              <Button sx={{ width: "100px" }} variant="outlined" color="primary" onClick={handleClickCancelDialogOpen}>
                Cancel
              </Button>
            </Box>
            <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
              <NCBidPDFExport bidChoiceData={bidChoiceData} buttonVariant="contained" />
            </Box>
            <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
              <Button
                sx={{
                  width: "100px",
                  backgroundColor: "secondary.swaGray",
                  "&:hover": {
                    backgroundColor: "secondary.swaGray5",
                  },
                }}
                variant="contained"
                onClick={handleClickGuideDialogOpen}
              >
                Guide
              </Button>
            </Box>
            <Box sx={{ paddingTop: "10px" }}>
              <Button
                sx={{ width: "100px" }}
                variant="contained"
                color="primary"
                onClick={handleClickSubmitDialogOpen}
                disabled={isSubmitButtonDisabled()}
              >
                Submit
              </Button>
            </Box>
          </>
        )}
      </>
    );
  };

  const handleAddChoice = () => {
    const existingChoices = [...selectedChoices];
    setSelectedChoices([...existingChoices, { date: "", timeOffType: "" }]);
    setTimeout(() => {
      const rowElem = document.querySelector(`[data-id="${selectedChoices.length + 1}"]`);
      if (rowElem) {
        rowElem.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 0);
    setTimeOffTypeErrors("");
  };

  const renderSubmitPTOFTOBidChoiceDialog = () => {
    return (
      <Dialog
        fullWidth
        maxWidth="sm"
        sx={{ margin: "auto" }}
        open={isSubmitDialogOpen}
        TransitionComponent={SlideUpTransition}
        keepMounted
      >
        {selectedChoices?.length === 0 ? (
          <>
            <DialogTitle>Submit a Blank Bid</DialogTitle>
            <DialogContent>
              <DialogContentText component={"div"}>
                <Grid container>
                  <div style={{ marginTop: "10px" }}>You are submitting a blank bid.</div>
                  <div style={{ marginTop: "10px" }}>Any choices previously submitted will be overriden.</div>
                  <div style={{ marginTop: "10px" }}>
                    You can continue to submit choices up until the bid window closes.
                  </div>
                </Grid>
              </DialogContentText>
            </DialogContent>
          </>
        ) : (
          <>
            <DialogTitle>Submit Bid Choices</DialogTitle>
            <DialogContent>
              <DialogContentText component={"div"}>
                <Grid container>
                  <div style={{ marginTop: "10px" }}>
                    You are submitting{" "}
                    <b>
                      {
                        selectedChoices.filter((choice) => choice.timeOffType === NC_PTO_FTO_BID_TIME_OFF_TYPES.PTO)
                          .length
                      }{" "}
                      choice
                      {selectedChoices.filter((choice) => choice.timeOffType === NC_PTO_FTO_BID_TIME_OFF_TYPES.PTO)
                        .length > 1 && "s"}
                    </b>
                    {" for "}
                    <b>
                      {ptoCount} PTO{ptoCount > 1 && "s"}
                    </b>
                    {" and "}
                    <b>
                      {
                        selectedChoices.filter((choice) => choice.timeOffType === NC_PTO_FTO_BID_TIME_OFF_TYPES.FTO)
                          .length
                      }{" "}
                      choice
                      {selectedChoices.filter((choice) => choice.timeOffType === NC_PTO_FTO_BID_TIME_OFF_TYPES.FTO)
                        .length > 1 && "s"}
                    </b>
                    {" for "}
                    <b>
                      {ftoCount} FTO{ftoCount > 1 && "s"}
                    </b>
                    .
                  </div>
                  <div style={{ marginTop: "10px" }}>Any choices previously submitted will be overriden.</div>
                  <div style={{ marginTop: "10px" }}>
                    You will see your submitted choices the next time you open this bid form again. You can keep editing
                    your choices until the bid window closes.
                  </div>
                </Grid>
              </DialogContentText>
            </DialogContent>
          </>
        )}
        <DialogActions>
          <ConfirmButton variant="outlined" color="primary" onClick={handleClickSubmitDialogClose}>
            Cancel
          </ConfirmButton>
          <ConfirmButton variant="contained" color="primary" onClick={handleSubmitClicked} isLoading={isSubmitLoading}>
            Submit
          </ConfirmButton>
        </DialogActions>
      </Dialog>
    );
  };

  const renderBidChoices = () => {
    return (
      <>
        <Grid
          container
          sx={{
            border: "solid 1px",
            borderColor: "secondary.swaGray2",
            backgroundColor: "secondary.swaGray2",
            paddingLeft: "1rem",
          }}
        >
          <Grid item xs={6} sx={{ display: "flex", justifyContent: "start", paddingTop: "6px" }}>
            <Typography sx={{ fontWeight: "bold" }}>
              {getNonEmptyChoices(selectedChoices).length}
              {` Choice${getNonEmptyChoices(selectedChoices).length > 1 ? "s" : ""}`} Entered
            </Typography>
          </Grid>

          <Grid item xs={6} sx={{ display: "flex", justifyContent: "end", paddingRight: "8px" }}>
            <Button sx={{ fontWeight: "bold", textDecoration: "underline" }} onClick={handleClickClearAllDialogOpen}>
              Clear All
            </Button>
            <ConfirmationDialog
              isDialogOpen={isClearAllDialogOpen}
              title={BID_FORM_CLEAR_ALL}
              message={BID_FORM_CLEAR_ALL_CHOICES_MESSAGE}
              handleNoButtonClicked={handleClickCancelClearAllDialogClose}
              handleYesButtonClicked={handleClearAllChoices}
              noTitle="Cancel"
              yesTitle="Clear All"
            />
          </Grid>
        </Grid>

        <Grid container>
          <Box sx={{ width: "inherit" }}>
            {
              <BidChoicesTable
                selectedChoices={selectedChoices}
                setSelectedChoices={setSelectedChoices}
                isDesktopView={true}
                isBidChoicesLoading={isBidChoicesLoading}
                bidChoiceData={bidChoiceData}
                disabledDates={disabledDates}
                bidType={bidChoiceData.bidType}
                timeOffTypeErrors={timeOffTypeErrors}
              />
            }
            <Divider />
            <Button
              className={classes.addChoice}
              variant="contained"
              onClick={handleAddChoice}
              disabled={!!selectedChoices.some((choice) => choice.date === "" || choice.timeOffType === "")}
            >
              Add Another Choice
            </Button>
          </Box>
        </Grid>
      </>
    );
  };

  const renderMobileNavBtnsForBidForm = () => {
    return (
      <>
        {isReadOnly ? (
          <Grid container justifyContent="space-between" direction="row" alignItems="center" sx={{ margin: "5px" }}>
            <Grid item xs={4} sx={{ padding: 1, display: "flex", justifyContent: "start" }}>
              <Box sx={{ paddingTop: "10px" }}>
                <NCBidPDFExport bidChoiceData={bidChoiceData} buttonVariant="outlined" />
              </Box>
            </Grid>
            <Grid item xs={8} sx={{ padding: 1, display: "flex", justifyContent: "end" }}>
              <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
                <Button
                  sx={{
                    width: "100px",
                  }}
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    history.push({
                      pathname: getMyBidsPath(),
                    })
                  }
                >
                  Close
                </Button>
              </Box>
            </Grid>
          </Grid>
        ) : (
          <>
            <Grid item xs={3} sx={{ padding: 1, display: "flex", justifyContent: "start", marginLeft: "10px" }}>
              <Button
                sx={{
                  width: "100px",
                  backgroundColor: "secondary.swaGray",
                  "&:hover": {
                    backgroundColor: "secondary.swaGray5",
                  },
                }}
                variant="contained"
                onClick={handleClickGuideDialogOpen}
              >
                Guide
              </Button>
            </Grid>
            <Grid item xs={8} sx={{ padding: 1, display: "flex", justifyContent: "end" }}>
              <Button sx={{ width: "100px" }} variant="outlined" color="primary" onClick={handleClickCancelDialogOpen}>
                Cancel
              </Button>
              <Button
                sx={{ width: "100px", marginLeft: "10px", marginRight: "-10px" }}
                variant="contained"
                color="primary"
                onClick={handleClickSubmitDialogOpen}
                disabled={isSubmitButtonDisabled()}
              >
                Submit
              </Button>
            </Grid>
          </>
        )}
      </>
    );
  };

  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.MY_BIDS, getBidName(bidChoiceData)];
  const breadcrumbsLinks = [HOME_PATH, `/${FEATURES.MY_BIDS}`];
  const renderDesktopPTOFTOBidChoicesPage = () => {
    if (isPageLoading || isScheduleLoading) {
      return <PageLoading />;
    }
    return (
      <>
        <StarsBreadcrumbs
          breadcrumbsList={breadcrumbsList}
          breadcrumbsLinks={breadcrumbsLinks}
          isConfirmationRequired={
            !isEqual(getNonEmptyChoices(selectedChoices), getNonEmptyChoices(initialSelectedChoices)) ||
            !isEqual(ftoCount, initialFtoCount) ||
            !isEqual(ptoCount, initialPtoCount)
          }
          cancelActionType={CANCEL_WARNING_MESSAGE_ACTION_TYPE.EDITS}
        />
        {/* Title & Cancel/Submit Buttons */}
        <Grid container>
          <Grid container justifyContent="space-between">
            <Grid item sx={{ paddingLeft: "2rem", paddingTop: "10px" }}>
              <Grid item>
                <Typography variant="h5" component="div" align="left">
                  {getBidName(bidChoiceData)} {bidChoiceData.bidType}
                </Typography>

                {getLastSubmittedByInfo()}

                <Box sx={{ textAlign: "left", paddingTop: "10px" }}>
                  {bidChoiceData && bidChoiceData.windowStartDate !== null && bidChoiceData.windowEndDate !== null && (
                    <Typography variant="body2" sx={{ color: "primary.swaRed", fontSize: "16px" }}>
                      <BidTimer
                        windowDates={[bidChoiceData.windowStartDate, bidChoiceData.windowEndDate]}
                        station={bidChoiceData.station}
                      />
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Stack>
              <Grid item sx={{ display: "flex", paddingRight: "2rem" }}>
                {renderDesktopNavBtnsForBidForm()}
              </Grid>
            </Stack>
          </Grid>
          <Box
            sx={{
              width: "100%",
              marginLeft: "2rem",
              marginRight: "2rem",
              marginTop: "1rem",
              border: "solid 1px",
              borderColor: "secondary.swaGray2",
            }}
          >
            {isReadOnly ? (
              <>
                <Grid
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                    backgroundColor: "secondary.swaLightestBlue",
                    paddingLeft: "1rem",
                    textAlign: "left",
                  }}
                >
                  <Grid sx={{ height: "40px", alignContent: "center" }}>
                    <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>
                      {`${ptoCount || 0} PTO${parseInt(ptoCount) > 1 ? "s" : ""} and ${ftoCount || 0} FTO${
                        parseInt(ftoCount) > 1 ? "s" : ""
                      } Requested & ${selectedChoices ? selectedChoices.length : 0} Choice${
                        selectedChoices?.length > 1 ? "s" : ""
                      } Entered`}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                  }}
                >
                  <Box
                    sx={{
                      height: `calc(85vh - 200px)`,
                      width: "inherit",
                      "& .choices.selected": { backgroundColor: "secondary.swaLightestBlue" },
                      "& .add-remove-icons.blocked": { backgroundColor: "secondary.swaGray2" },
                      "& .add-remove-icons.addIcon": { backgroundColor: "secondary.swaBlue" },
                    }}
                  >
                    <BidChoicesTable
                      selectedChoices={selectedChoices}
                      setSelectedChoices={setSelectedChoices}
                      isDesktopView={true}
                      isBidChoicesLoading={isBidChoicesLoading}
                      bidChoiceData={bidChoiceData}
                      isReadOnly={true}
                      disabledDates={disabledDates}
                      bidType={bidChoiceData.bidType}
                    />
                  </Box>
                </Grid>
              </>
            ) : (
              <>
                <Grid container sx={{ paddingTop: "10px" }}>
                  <Grid
                    item
                    sx={{
                      paddingLeft: "1rem",
                      fontWeight: "bold",
                      fontSize: "14px",
                      textAlign: "left",
                      marginTop: "15px",
                    }}
                  >
                    Enter the # of PTOs you would like to use (0-31):
                  </Grid>
                  <Grid item>
                    <div className={classes.bidInput}>
                      <TextField
                        type="text"
                        data-testid="choices-count-textField"
                        className={classes.inputText}
                        variant="outlined"
                        value={ptoCount}
                        onChange={handlePTOCountChange}
                        onBlur={handlePTOCountBlur}
                        onKeyUp={handlePTOCountEnterKeyPressed}
                        inputProps={{ inputMode: "numeric", maxLength: 2 }}
                        error={ptoCountErrors !== ""}
                        helperText={ptoCountErrors}
                      />
                    </div>
                  </Grid>
                </Grid>
                <Grid container sx={{ paddingTop: "10px" }}>
                  <Grid
                    item
                    sx={{
                      paddingLeft: "1rem",
                      fontWeight: "bold",
                      fontSize: "14px",
                      textAlign: "left",
                      marginTop: "15px",
                    }}
                  >
                    Enter the # of FTOs you would like to use (0-10):
                  </Grid>
                  <Grid item>
                    <div className={classes.bidInput}>
                      <TextField
                        type="text"
                        data-testid="choices-count-textField"
                        className={classes.inputText}
                        variant="outlined"
                        value={ftoCount}
                        onChange={handleFTOCountChange}
                        onBlur={handleFTOCountBlur}
                        onKeyUp={handleFTOCountEnterKeyPressed}
                        inputProps={{ inputMode: "numeric", maxLength: 2 }}
                        error={ftoCountErrors !== ""}
                        helperText={ftoCountErrors}
                      />
                    </div>
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    width: "100%",
                    margin: "auto",
                    marginTop: "10px",
                  }}
                >
                  {renderBidChoices()}
                </Box>
              </>
            )}
          </Box>
        </Grid>
        {renderDialogs()}
      </>
    );
  };

  const getLastSubmittedByInfo = () => {
    if (getLastSubmittedTimestamp() !== "") {
      if (
        lastUpdatedByFirstName &&
        lastUpdatedByLastName &&
        lastUpdatedByFirstName !== "" &&
        lastUpdatedByLastName !== ""
      ) {
        return (
          <Grid className={classes.lastSubmitted}>
            {`${LAST_UPDATED}${lastUpdatedByLastName}, ${lastUpdatedByFirstName} ${getLastUpdatedTimeString()}`}
          </Grid>
        );
      } else {
        return (
          <Grid className={classes.lastSubmitted}>{`${LAST_UPDATED_TIMESTAMP} ${getLastUpdatedTimeString()}`}</Grid>
        );
      }
    }
    return "";
  };

  const renderMobilePTOFTOBidChoicesPage = () => {
    if (isPageLoading) {
      return <PageLoading />;
    }
    return (
      <>
        {/* Title */}
        <Grid container>
          <Grid container justifyContent="space-between">
            <Grid item sx={{ paddingLeft: "1rem", paddingTop: "20px" }}>
              <Typography variant="h5" component="div" align="left">
                {getBidName(bidChoiceData)} {bidChoiceData.bidType}
              </Typography>

              {getLastSubmittedByInfo()}

              <Box sx={{ textAlign: "left", paddingTop: "10px" }}>
                {bidChoiceData && bidChoiceData.windowStartDate !== null && bidChoiceData.windowEndDate !== null && (
                  <Typography variant="body2" sx={{ color: "primary.swaRed", fontSize: "12px" }}>
                    <Grid container spacing={0.5}>
                      <Grid item>
                        <AccessTimeIcon fontSize="12px" />
                      </Grid>
                      <Grid item xs={11.2} sm={11.2} style={{ fontWeight: "bold", fontSize: "10px" }}>
                        <BidTimer
                          windowDates={[bidChoiceData.windowStartDate, bidChoiceData.windowEndDate]}
                          station={bidChoiceData.station}
                        />
                      </Grid>
                    </Grid>
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ width: "100%", margin: "auto", marginTop: "10px" }}>
            {isReadOnly ? (
              <>
                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                    backgroundColor: "secondary.swaLightestBlue",
                    paddingLeft: "1rem",
                    textAlign: "left",
                  }}
                >
                  <Grid>
                    <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>
                      {`${ptoCount || 0} PTO${parseInt(ptoCount) > 1 ? "s" : ""} and ${ftoCount || 0} FTO${
                        parseInt(ftoCount) > 1 ? "s" : ""
                      } Requested & ${selectedChoices ? selectedChoices.length : 0} Choice${
                        selectedChoices?.length > 1 ? "s" : ""
                      } Entered`}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                  }}
                >
                  <Box sx={{ width: "inherit" }}>
                    <BidChoicesTable
                      selectedChoices={selectedChoices}
                      setSelectedChoices={setSelectedChoices}
                      isDesktopView={false}
                      isBidChoicesLoading={isBidChoicesLoading}
                      bidChoiceData={bidChoiceData}
                      isReadOnly={true}
                      disabledDates={disabledDates}
                      bidType={bidChoiceData.bidType}
                    />
                  </Box>
                </Grid>
              </>
            ) : (
              <>
                <Divider />
                <Grid container sx={{ paddingTop: "10px" }}>
                  <Grid
                    item
                    xs={7.5}
                    sx={{
                      paddingLeft: "1rem",
                      fontWeight: "bold",
                      fontSize: "14px",
                      textAlign: "left",
                      marginTop: "10px",
                    }}
                  >
                    Enter the # of PTOs you would like to use (0-31):
                  </Grid>
                  <Grid item xs={4.5} sx={{ display: "flex" }}>
                    <div className={classes.bidInput}>
                      <TextField
                        type="text"
                        data-testid="choices-count-textField"
                        className={classes.inputText}
                        variant="outlined"
                        value={ptoCount}
                        onChange={handlePTOCountChange}
                        onBlur={handlePTOCountBlur}
                        onKeyUp={handlePTOCountEnterKeyPressed}
                        inputProps={{ inputMode: "numeric", maxLength: 2 }}
                        error={ptoCountErrors !== ""}
                        helperText={ptoCountErrors}
                      />
                    </div>
                  </Grid>
                </Grid>
                <Grid container sx={{ paddingTop: "10px" }}>
                  <Grid
                    item
                    xs={7.5}
                    sx={{
                      paddingLeft: "1rem",
                      fontWeight: "bold",
                      fontSize: "14px",
                      textAlign: "left",
                      marginTop: "10px",
                    }}
                  >
                    Enter the # of FTOs you would like to use (0-10):
                  </Grid>
                  <Grid item xs={4.5} sx={{ display: "flex" }}>
                    <div className={classes.bidInput}>
                      <TextField
                        type="text"
                        data-testid="choices-count-textField"
                        className={classes.inputText}
                        variant="outlined"
                        value={ftoCount}
                        onChange={handleFTOCountChange}
                        onBlur={handleFTOCountBlur}
                        onKeyUp={handleFTOCountEnterKeyPressed}
                        inputProps={{ inputMode: "numeric", maxLength: 2 }}
                        error={ftoCountErrors !== ""}
                        helperText={ftoCountErrors}
                      />
                    </div>
                  </Grid>
                </Grid>
                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                    backgroundColor: "secondary.swaGray2",
                    paddingLeft: "1rem",
                    marginTop: 1,
                  }}
                >
                  <Grid item xs={6} sx={{ display: "flex", justifyContent: "start", paddingTop: "6px" }}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      {getNonEmptyChoices(selectedChoices).length}
                      {` Choice${getNonEmptyChoices(selectedChoices).length > 1 ? "s" : ""}`} Entered
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sx={{ display: "flex", justifyContent: "end", paddingRight: "8px" }}>
                    <Button
                      sx={{ fontWeight: "bold", textDecoration: "underline" }}
                      onClick={handleClickClearAllDialogOpen}
                    >
                      Clear All
                    </Button>
                    <ConfirmationDialog
                      isDialogOpen={isClearAllDialogOpen}
                      title={BID_FORM_CLEAR_ALL}
                      message={BID_FORM_CLEAR_ALL_CHOICES_MESSAGE}
                      handleNoButtonClicked={handleClickCancelClearAllDialogClose}
                      handleYesButtonClicked={handleClearAllChoices}
                      noTitle="Cancel"
                      yesTitle="Clear All"
                    />
                  </Grid>
                </Grid>

                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                    borderBottom: 0,
                  }}
                >
                  <Box sx={{ width: "inherit" }}>
                    <BidChoicesTable
                      selectedChoices={selectedChoices}
                      setSelectedChoices={setSelectedChoices}
                      isDesktopView={false}
                      isBidChoicesLoading={isBidChoicesLoading}
                      bidChoiceData={bidChoiceData}
                      disabledDates={disabledDates}
                      bidType={bidChoiceData.bidType}
                      timeOffTypeErrors={timeOffTypeErrors}
                    />
                  </Box>
                </Grid>
                <Divider />
                <Button
                  className={classes.addChoice}
                  variant="contained"
                  onClick={handleAddChoice}
                  disabled={!!selectedChoices.some((choice) => choice.date === "" || choice.timeOffType === "")}
                >
                  Add Another Choice
                </Button>
                <Divider />
              </>
            )}
          </Box>

          <Grid
            container
            sx={{
              marginTop: "5px",
              bottom: 0,
              position: "fixed",
              backgroundColor: "secondary.swaWhite",
              zIndex: 999,
            }}
          >
            {renderMobileNavBtnsForBidForm()}
          </Grid>
        </Grid>
        {renderDialogs()}
      </>
    );
  };

  const renderDialogs = () => {
    return (
      <>
        <ConfirmationDialog
          isDialogOpen={isExitDialogOpen}
          title="Cancel"
          message={BID_FORM_CANCEL_MESSAGE}
          handleNoButtonClicked={handleClickCancelDialogClose}
          redirectTo={getMyBidsPath()}
        />
        {renderSubmitPTOFTOBidChoiceDialog()}
        <BidInfoDialog
          isDialogOpen={isGuideDialogOpen}
          title="Guide"
          handleCloseClicked={handleCloseClickedGuideDialog}
          isDesktopView={props.isDesktopView}
          eid={userSession.eid}
          bidChoiceData={bidChoiceData}
          lastSubmittedTimestamp={getLastSubmittedTimestamp()}
          bidType={bidChoiceData.bidType}
        />
        <SuccessDialog
          isSuccessDialogOpen={isSubmitResponseDialogOpen}
          title={PTO_FTO_BID_CHOICE_CONFIRMATION_TITLE}
          message={PTO_FTO_BID_CHOICE_CONFIRMATION_MESSAGE}
          hasError={submitErrors !== ""}
          error={BID_CHOICE_SAVE_ERROR_MESSAGE}
          redirectTo={submitErrors === "" && getMyBidsPath()}
          handleOKClick={() => setIsSubmitResponseDialogOpen(false)}
        />
      </>
    );
  };

  return (
    <div className="App-contents enable-scroll" data-testid="pto-fto-bid-choices-page">
      {props.isDesktopView ? renderDesktopPTOFTOBidChoicesPage() : renderMobilePTOFTOBidChoicesPage()}
    </div>
  );
};

NonContractPTOFTOBidChoices.propTypes = {
  isDesktopView: PropTypes.bool,
  featureConfig: PropTypes.object.isRequired,
};

export default NonContractPTOFTOBidChoices;
